import { useLocalization } from "../../hooks/useLocalization";
import { statTierClass, type StatTier } from "../../utils/playerStatTiers";

const LEGEND_TIERS: Exclude<StatTier, "neutral">[] = [
  "excellent",
  "good",
  "average",
  "low",
  "poor",
];

interface StatTierLegendProps {
  className?: string;
}

/** Used on player match history + season stats tables */
export const StatTierLegend = ({ className = "" }: StatTierLegendProps) => {
  const { strings } = useLocalization();
  const copy = strings.STAT_TIER_LEGEND;

  return (
    <aside
      className={`border-t border-t-white/[0.08] px-5 py-4 text-[0.82rem] leading-[1.45] text-[#8b8b9a] ${className}`}
      aria-label={copy.TITLE}
    >
      <p className="mb-[0.35rem] text-[0.85rem] font-semibold text-[#c8c8d8]">{copy.TITLE}</p>
      <p className="mb-3 max-w-[52rem]">{copy.INTRO}</p>

      <ul className="mb-[0.85rem] flex list-none flex-wrap gap-x-[0.85rem] gap-y-2 p-0">
        {LEGEND_TIERS.map((tier) => (
          <li key={tier}>
            <span className={`${statTierClass(tier)} inline-block text-[0.85rem]`}>
              {copy.LABELS[tier]}
            </span>
          </li>
        ))}
      </ul>

      <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-x-4 gap-y-[0.35rem]">
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.KDA}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.KDA}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.K}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.K}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.DEATHS}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.D}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.ASSISTS}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.A}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.CSM}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.CSM}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.KP}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.KP}
          </dd>
        </div>
        <div className="grid grid-cols-[3.25rem_1fr] items-baseline gap-2">
          <dt className="m-0 font-semibold tabular-nums text-[#a8a8bc]">{copy.STATS.DPM}</dt>
          <dd className="m-0 font-jetbrains text-[0.78rem] tabular-nums text-[#7a7a92]">
            {copy.THRESHOLDS.DPM}
          </dd>
        </div>
      </dl>
    </aside>
  );
};
